import React from 'react';

// Device detail — the full page a Stock row opens (design 2a in
// "Device Detail.dc.html").
//
// This replaces the inline `skSel` block that used to render inside the Stock
// view: a 720px-wide column with four tabs, which the redesign widens to the
// page and regroups. The DeviceSidebar drawer is untouched and still serves the
// quick peek from Dashboard, Locations, Components and the Map.
//
// Two of the design's panels are not rendered:
//   - "Issued with" (consumables). Nothing in st.consumableStock links an item
//     to a device, so the card would only ever be empty and would imply a
//     feature that does not exist.
//   - The mockup's map thumbnail. Position lives on the Device Map page.
//
// Props are all passed down from App: the page owns no data of its own, so it
// stays correct as st.devices changes underneath it.
export default function DeviceDetailPage({
  dev, st, onBack,
  getStats, getHealthScore, getDefs, getPF,
  warrantyLabel, locName, SYS_LOCS,
  lightbugLive, onFetchLightbug,
  onMoveLocation, onAddToRma, onRunTest, onOpenQcBatch, onRetire, onExport,
  isReadOnly, isManager, RC, IC,
}) {
  const [tab, setTab] = React.useState('overview');

  const stats = getStats(dev);
  const hs = getHealthScore(dev.id);
  const defs = getDefs(dev.type);
  const pfDefs = getPF(dev.type);

  const allRes = React.useMemo(() => (st.testResults || [])
    .filter(r => r.deviceId === dev.id)
    .sort((a, b) => new Date(b.date) - new Date(a.date)), [st.testResults, dev.id]);
  const allFwRes = React.useMemo(() => (st.firmwareTestResults || [])
    .filter(r => r.deviceId === dev.id)
    .sort((a, b) => new Date(b.date) - new Date(a.date)), [st.firmwareTestResults, dev.id]);
  const latest = allRes[0] || null;

  const maintLogs = React.useMemo(() => (st.maintenanceJobs || [])
    .filter(j => j.deviceId === dev.id)
    .flatMap(j => (j.maintenanceLog || []).map(e => ({ ...e, jobId: j.id, jobTier: j.tier, actions: e.actions || [] })))
    .sort((a, b) => new Date(b.date) - new Date(a.date)), [st.maintenanceJobs, dev.id]);

  // Every RMA this device has been on, newest first, so the Quarantine and RMA
  // tab shows the history rather than only the open one.
  const rmas = React.useMemo(() => (st.rmaShipments || [])
    .filter(sh => (sh.deviceIds || []).includes(dev.id))
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)), [st.rmaShipments, dev.id]);

  // One merged, date-sorted stream for the Activity tab.
  const timeline = React.useMemo(() => [
    ...allRes.map(r => {
      const failed = pfDefs.some(t => r.checks?.[t.id] === 'fail');
      const passed = pfDefs.length > 0 && pfDefs.every(t => r.checks?.[t.id] === 'pass');
      return {
        date: r.date,
        title: 'Bench test — ' + (failed ? 'Fail' : passed ? 'Pass' : 'Partial'),
        meta: (r.tester || 'Unknown') + (r.notes ? ' · ' + r.notes.slice(0, 60) : ''),
        dot: failed ? 'var(--danger-text)' : passed ? 'var(--success-text)' : 'var(--warning-text)',
      };
    }),
    ...maintLogs.map(m => ({
      date: m.date,
      title: 'Maintenance' + (m.actions.length ? ' — ' + m.actions.slice(0, 2).join(', ') : ''),
      meta: m.description ? m.description.slice(0, 70) : 'By ' + (m.by || 'Unknown'),
      dot: 'var(--info-text)',
    })),
    ...(dev.locationLog || []).map(l => ({
      date: l.date,
      title: (l.from || '—') + ' → ' + l.to,
      meta: 'By ' + (l.by || 'System'),
      dot: 'var(--text-muted)',
    })),
    ...(dev.qc?.checkedAt ? [{
      date: dev.qc.checkedAt,
      title: 'Goods-in QC — ' + (dev.qc.result === 'pass' ? 'Passed' : 'Failed'),
      meta: (dev.importBatch ? 'Batch ' + dev.importBatch + ' · ' : '') + (dev.qc.by || 'Unknown'),
      dot: dev.qc.result === 'pass' ? 'var(--success-text)' : 'var(--danger-text)',
    }] : []),
  ].filter(e => e.date).sort((a, b) => new Date(b.date) - new Date(a.date)),
  [allRes, maintLogs, dev.locationLog, dev.qc, dev.importBatch, pfDefs]);

  const fmt = d => (d ? new Date(d).toLocaleDateString('en-GB') : '—');
  const fmtDT = d => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

  const STATUS_PILL = {
    'Not Started': 'neutral', 'In Testing': 'info', Passed: 'success', Failed: 'danger',
    Quarantine: 'warning', 'Pending Retirement': 'warning', Retired: 'neutral',
    'Pending RMA': 'warning', 'With Manufacturer': 'info', 'In Stock': 'success',
  };
  const statusTone = STATUS_PILL[dev.status] || 'neutral';

  const warranty = dev.purchaseDate ? warrantyLabel(dev.purchaseDate, st.rmaSettings?.warrantyMonths) : null;
  const lb = dev.deviceId ? lightbugLive?.[String(dev.deviceId)] : null;

  // How long it has sat where it is. The last locationLog hop is the move that
  // put it here, so its date is the "since".
  const lastMove = (dev.locationLog || [])[(dev.locationLog || []).length - 1] || null;
  const sinceDays = lastMove?.date
    ? Math.floor((Date.now() - new Date(lastMove.date).getTime()) / 86400000)
    : null;

  const openRma = rmas.find(sh => sh.deviceIds?.includes(dev.id) && sh.stage && sh.stage !== 'closed') || null;

  const card = { background: 'var(--surface-default)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' };
  const cardHead = { height: 44, display: 'flex', alignItems: 'center', gap: 10, padding: '0 16px', borderBottom: '1px solid var(--border-default)' };
  const cardTitle = { fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' };

  const identity = [
    ['Serial', dev.serial, false],
    ['Lightbug device ID', dev.deviceId || 'Not recorded', !dev.deviceId],
    ['Type', dev.type, false],
    ['Firmware', dev.firmware || 'Not recorded', !dev.firmware],
    ['SIM 1', dev.simType || '—', !dev.simType],
    ...(dev.type === 'RH2' ? [['SIM 2', dev.simType2 || '—', !dev.simType2]] : []),
    ['Import batch', dev.importBatch || '—', !dev.importBatch],
    ['Purchase date', dev.purchaseDate ? fmt(dev.purchaseDate) + (dev.purchaseDateBackfilled ? ' (back-filled)' : '') : '—', !dev.purchaseDate],
    ['Warranty term', (st.rmaSettings?.warrantyMonths ?? 12) + ' months · RMA settings', false],
    ['Added to Bench', dev.added ? fmt(dev.added) + (dev.qc ? ' · goods-in QC' : '') : '—', !dev.added],
    ['Last tested', stats.last ? fmt(stats.last) : 'Never', !stats.last],
  ];

  const whereIs = [
    ['Status', dev.status || '—'],
    ['Location', dev.location || 'Not set'],
    ['Since', lastMove?.date ? fmt(lastMove.date) + (sinceDays !== null ? ' · ' + sinceDays + ' day' + (sinceDays === 1 ? '' : 's') : '') : '—'],
    ['Moved by', lastMove?.by || '—'],
    ['On an RMA', openRma ? (openRma.ref || openRma.id) : (dev.status === 'Pending RMA' ? 'Not yet — staged only' : 'No')],
  ];

  const telemetry = [
    { label: 'Connection', value: lb ? (lb.active ? 'Online' : 'Offline') : '—', fg: lb ? (lb.active ? 'var(--success-text)' : 'var(--danger-text)') : 'var(--text-muted)' },
    { label: 'Battery', value: lb?.batteryPct != null ? lb.batteryPct + '%' : (typeof lb?.batteryVoltage === 'number' ? lb.batteryVoltage.toFixed(2) + ' V' : '—'), fg: 'var(--text-primary)' },
    { label: 'Fix type', value: lb?.rtkStatus?.fixType || lb?.rtkStatus?.status || lb?.rtkStatus?.mode || '—', fg: 'var(--text-primary)' },
    { label: 'Last seen', value: lb?.lastSeen ? fmt(lb.lastSeen) : '—', fg: 'var(--text-primary)' },
  ];

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'tests', label: 'Bench tests', count: allRes.length + allFwRes.length },
    { id: 'rma', label: 'Quarantine and RMA', count: rmas.length + (dev.status === 'Pending RMA' || dev.status === 'Quarantine' ? 1 : 0) },
    { id: 'maintenance', label: 'Maintenance', count: maintLogs.length },
    { id: 'activity', label: 'Activity', count: timeline.length },
  ];

  // The banner the design leads with. It only appears when there is something
  // genuinely wrong, so a healthy device starts on Identity.
  const alert = dev.qc && dev.qc.result === 'fail'
    ? {
      title: 'Failed goods-in QC' + (dev.qc.failedChecks?.length ? ' · ' + dev.qc.failedChecks.join(', ') : ''),
      body: [dev.qc.note, dev.qc.by ? 'Checked by ' + dev.qc.by + (dev.qc.checkedAt ? ' on ' + fmt(dev.qc.checkedAt) : '') : '']
        .filter(Boolean).join(' '),
    }
    : (dev.status === 'Quarantine' && (dev.quarantineFailedChecks?.length || dev.quarantineNote))
      ? {
        title: 'In quarantine' + (dev.quarantineFailedChecks?.length ? ' · ' + dev.quarantineFailedChecks.join(', ') : ''),
        body: [dev.quarantineNote, dev.quarantineDate ? 'Since ' + fmt(dev.quarantineDate) : ''].filter(Boolean).join(' · '),
      }
      : null;

  const kv = (rows, keyWidth) => (
    <div style={{ padding: 16, display: 'grid', gridTemplateColumns: keyWidth ? '1fr' : 'repeat(2,minmax(0,1fr))', gap: '12px 32px' }}>
      {rows.map(([k, v, muted]) => (
        <div key={k} style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
          <span style={{ width: keyWidth || 132, flexShrink: 0, fontSize: 12, color: 'var(--text-muted)' }}>{k}</span>
          <span className="tnum" style={{ flex: 1, minWidth: 0, fontSize: 14, color: muted ? 'var(--text-muted)' : 'var(--text-primary)', wordBreak: 'break-word' }}>{v}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <button onClick={onBack} aria-label="Back to stock"
          style={{ width: 32, height: 32, flexShrink: 0, border: '1px solid var(--border-default)', borderRadius: 6, background: 'var(--surface-default)', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
          </svg>
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span className="tnum" style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.4px', color: 'var(--text-primary)' }}>{dev.serial}</span>
            <span style={{ display: 'inline-flex', fontSize: 12, color: 'var(--text-muted)', background: 'var(--surface-inset)', border: '1px solid var(--border-default)', padding: '2px 8px', borderRadius: 4 }}>{dev.type}</span>
            <span className={'pill pill-' + statusTone + ' pill-dot'}>{dev.status}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, fontSize: 14, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
            {dev.deviceId && <span>Lightbug {dev.deviceId}</span>}
            {dev.deviceId && dev.importBatch && <span style={{ color: 'var(--border-strong)' }}>·</span>}
            {dev.importBatch && <span>Batch {dev.importBatch}</span>}
            {warranty && <span style={{ color: 'var(--border-strong)' }}>·</span>}
            {warranty && <span className={warranty.pillClass}>{warranty.text}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {onExport && <button className="btn btn-ghost btn-sm" onClick={() => onExport(dev)}>Export</button>}
          {onMoveLocation && !isReadOnly && (
            <button className="btn btn-ghost btn-sm" onClick={() => onMoveLocation(dev)}>Move to location</button>
          )}
          {onAddToRma && !isReadOnly && dev.status !== 'Retired' && (
            <button className="btn btn-primary btn-sm" onClick={() => onAddToRma(dev)}>Add to RMA</button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: 0 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={'tab' + (tab === t.id ? ' tab-active' : '')}>
            {t.label}
            {t.count > 0 && (
              <span className="tnum" style={{ marginLeft: 8, fontSize: 12, color: 'var(--text-muted)', background: 'var(--surface-inset)', borderRadius: 9999, padding: '1px 7px' }}>{t.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW ──────────────────────────────────────────────────────── */}
      {tab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 360px', gap: 20, alignItems: 'start' }}>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>

            {alert && (
              <div style={{ display: 'flex', gap: 12, padding: '14px 16px', border: '1px solid var(--danger-border)', background: 'var(--danger-fill)', borderRadius: 'var(--radius-lg)' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--danger-text)' }}>{alert.title}</div>
                  {alert.body && <div style={{ fontSize: 14, lineHeight: '18px', color: 'var(--danger-text)', marginTop: 4 }}>{alert.body}</div>}
                  {dev.importBatch && onOpenQcBatch && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => onOpenQcBatch(dev.importBatch)}>
                        Open batch {dev.importBatch}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Health score */}
            {hs && (
              <div style={card}>
                <div style={cardHead}>
                  <span style={cardTitle}>Health score</span>
                  <span title={'Score formula: base = weighted pass rate scaled by test confidence (min 70 baseline). Penalties: −8 per maintenance event (max −25), −3× avg reboots (max −10). Final = max(0, base − penalties).\n\nGrades: Excellent ≥90 · Good ≥70 · Fair ≥50 · Poor ≥30 · Critical <30'}
                    style={{ fontSize: 12, color: 'var(--text-ghost)', cursor: 'help', userSelect: 'none' }}>&#9432;</span>
                  <div style={{ flex: 1 }} />
                  <span className="tnum" style={{ fontSize: 22, fontWeight: 800, color: hs.color, lineHeight: 1 }}>
                    {hs.score}<span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>/100</span>
                  </span>
                </div>
                <div style={{ padding: 16 }}>
                  <div style={{ height: 8, borderRadius: 4, background: 'var(--surface-inset)', overflow: 'hidden', marginBottom: 10 }}>
                    <div style={{ height: '100%', width: hs.score + '%', background: hs.color, borderRadius: 4 }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: hs.color }}>{hs.grade}</span>
                    <div style={{ display: 'flex', gap: 12, fontSize: 11, color: 'var(--text-muted)' }}>
                      <span>{hs.tests} test{hs.tests !== 1 ? 's' : ''}</span>
                      <span>{hs.passTests} passed</span>
                      {hs.maintCount > 0 && <span style={{ color: 'var(--info-text)' }}>{hs.maintCount} maintenance</span>}
                      {hs.avgReboots > 0 && <span style={{ color: 'var(--warning-text)' }}>{hs.avgReboots} avg reboots</span>}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Identity */}
            <div style={card}>
              <div style={cardHead}><span style={cardTitle}>Identity</span></div>
              {kv(identity)}
            </div>

            {/* Telemetry. Only for units with a Lightbug id — the card would
                otherwise be four dashes with no way to ever fill them. */}
            {dev.deviceId && (
              <div style={card}>
                <div style={cardHead}>
                  <span style={cardTitle}>Telemetry</span>
                  <div style={{ flex: 1 }} />
                  <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>via Lightbug</span>
                  {onFetchLightbug && (
                    <button className="btn btn-ghost btn-sm" onClick={() => onFetchLightbug(dev.deviceId)} title="Refresh Lightbug data">&#8635;</button>
                  )}
                </div>
                <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(0,1fr))', gap: 12 }}>
                    {telemetry.map(t => (
                      <div key={t.label} style={{ border: '1px solid var(--border-default)', borderRadius: 6, padding: 12 }}>
                        <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)' }}>{t.label}</div>
                        <div className="tnum" style={{ fontSize: 20, fontWeight: 500, lineHeight: 1, color: t.fg, marginTop: 8 }}>{t.value}</div>
                      </div>
                    ))}
                  </div>
                  {!lb && (
                    <div style={{ padding: 12, border: '1px dashed var(--border-strong)', borderRadius: 6, fontSize: 14, lineHeight: '18px', color: 'var(--text-muted)' }}>
                      No live data for this device. Nothing has been fetched from Lightbug this session — use the refresh button above.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Bench test — the latest run, or a clear statement that there is none */}
            <div style={card}>
              <div style={cardHead}>
                <span style={cardTitle}>Bench test</span>
                <span className={'pill pill-' + (stats.tested ? (stats.overall === 'pass' ? 'success' : stats.overall === 'fail' ? 'danger' : 'warning') : 'neutral') + ' pill-dot'}>
                  {stats.tested ? (stats.overall === 'pass' ? 'Passed' : stats.overall === 'fail' ? 'Failed' : 'Partial') : 'Never tested'}
                </span>
                <div style={{ flex: 1 }} />
                {onRunTest && !isReadOnly && (
                  <button className="btn btn-ghost btn-sm" onClick={() => onRunTest(dev)}>Run the {dev.type} test</button>
                )}
              </div>
              {!latest && (
                <div style={{ padding: '12px 16px', background: 'var(--surface-inset)', borderBottom: '1px solid var(--border-subtle)', fontSize: 14, lineHeight: '18px', color: 'var(--text-muted)' }}>
                  No bench test on record, so the {defs.length} {dev.type} check{defs.length === 1 ? '' : 's'} below have never been run against this unit.
                </div>
              )}
              {latest && (
                <div style={{ padding: '12px 16px', background: 'var(--surface-inset)', borderBottom: '1px solid var(--border-subtle)', fontSize: 12, color: 'var(--text-muted)' }}>
                  Tested by {latest.tester} · {fmtDT(latest.date)}
                </div>
              )}
              {defs.map(t => {
                const v = latest ? latest.checks?.[t.id] : undefined;
                return (
                  <div key={t.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 92px', gap: 12, minHeight: 40, alignItems: 'center', padding: '6px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, minWidth: 0 }}>
                      <span style={{ fontSize: 14, color: 'var(--text-primary)', flexShrink: 0 }}>{t.label}</span>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.desc}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      {t.type === 'passfail'
                        ? (v
                          ? <span className={'pill pill-' + (v === 'pass' ? 'success' : v === 'fail' ? 'danger' : 'neutral') + (v === 'na' ? '' : ' pill-dot')}>{RC?.[v]?.l || v}</span>
                          : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Not run</span>)
                        : <span className="tnum" style={{ fontSize: 14, fontWeight: 600, color: v != null ? 'var(--warning-text)' : 'var(--text-muted)' }}>{v != null ? v : '—'}</span>}
                    </div>
                  </div>
                );
              })}
              {latest?.notes && (
                <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--text-primary)', background: 'var(--surface-inset)' }}>
                  <strong style={{ fontWeight: 600 }}>Notes:</strong> {latest.notes}
                </div>
              )}
            </div>
          </div>

          {/* Right column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            <div style={card}>
              <div style={cardHead}><span style={cardTitle}>Where it is</span></div>
              {kv(whereIs, 104)}
            </div>

            {/* Actions. Each is gated on the same rules as the rest of the app —
                read-only users see none, and retire is managers-only. */}
            {!isReadOnly && (
              <div style={card}>
                <div style={cardHead}><span style={cardTitle}>Actions</span></div>
                <div style={{ padding: 8 }}>
                  {[
                    { label: 'Move to location', onClick: onMoveLocation && (() => onMoveLocation(dev)) },
                    { label: 'Run a bench test', onClick: onRunTest && (() => onRunTest(dev)) },
                    { label: 'Add to RMA', onClick: onAddToRma && dev.status !== 'Retired' && (() => onAddToRma(dev)) },
                    {
                      label: isManager ? 'Retire' : 'Request retirement',
                      onClick: onRetire && dev.status !== 'Retired' && (() => onRetire(dev)),
                      destructive: true, divider: true,
                      note: isManager ? null : 'Needs a manager',
                    },
                  ].filter(a => a.onClick).map(a => (
                    <button key={a.label} onClick={a.onClick}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, width: '100%', height: 36, padding: '0 12px',
                        borderRadius: 6, border: 'none', borderTop: a.divider ? '1px solid var(--border-subtle)' : 'none',
                        background: 'transparent', fontFamily: 'inherit', fontSize: 14, cursor: 'pointer', textAlign: 'left',
                        color: a.destructive ? 'var(--danger-text)' : 'var(--text-primary)',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-raised)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                      <span style={{ flex: 1 }}>{a.label}</span>
                      {a.note && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{a.note}</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Activity preview — the full stream is its own tab */}
            <div style={card}>
              <div style={cardHead}>
                <span style={cardTitle}>Activity</span>
                <div style={{ flex: 1 }} />
                {timeline.length > 4 && (
                  <button className="btn btn-ghost btn-sm" onClick={() => setTab('activity')}>All {timeline.length}</button>
                )}
              </div>
              <div style={{ padding: 16 }}>
                {timeline.length === 0
                  ? <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Nothing recorded yet.</div>
                  : timeline.slice(0, 4).map((e, i, arr) => (
                    <div key={i} style={{ display: 'flex', gap: 12 }}>
                      <div style={{ width: 16, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ width: 9, height: 9, borderRadius: 9999, background: e.dot, marginTop: 5, flexShrink: 0 }} />
                        {i < arr.length - 1 && <div style={{ flex: 1, width: 1, background: 'var(--border-default)', minHeight: 16 }} />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0, paddingBottom: i < arr.length - 1 ? 14 : 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>{e.title}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{fmt(e.date)} · {e.meta}</div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── BENCH TESTS ───────────────────────────────────────────────────── */}
      {tab === 'tests' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {allRes.length + allFwRes.length === 0 ? (
            <div style={{ ...card, padding: 28, textAlign: 'center', fontSize: 14, color: 'var(--text-muted)' }}>
              No bench test has been run against this unit.
            </div>
          ) : [...allRes.map(r => ({ ...r, fw: false })), ...allFwRes.map(r => ({ ...r, fw: true }))]
            .sort((a, b) => new Date(b.date) - new Date(a.date))
            .map(r => {
              const pc = pfDefs.filter(t => r.checks?.[t.id] === 'pass').length;
              const fc = pfDefs.filter(t => r.checks?.[t.id] === 'fail').length;
              const overall = fc > 0 ? 'fail' : pc === pfDefs.length && pfDefs.length > 0 ? 'pass' : null;
              return (
                <div key={r.id} style={card}>
                  <div style={{ ...cardHead, flexWrap: 'wrap' }}>
                    {r.fw && <span className="pill pill-brand" style={{ fontSize: 11 }}>Firmware {r.firmwareVersion}</span>}
                    <span style={{ fontSize: 14, color: 'var(--text-primary)', fontWeight: 500 }}>{fmtDT(r.date)}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.tester}</span>
                    <div style={{ flex: 1 }} />
                    <span className="tnum" style={{ fontSize: 12, color: 'var(--success-text)', fontWeight: 600 }}>{pc} passed</span>
                    <span className="tnum" style={{ fontSize: 12, color: fc ? 'var(--danger-text)' : 'var(--text-muted)', fontWeight: 600 }}>{fc} failed</span>
                    {overall && <span className={'pill pill-' + (overall === 'pass' ? 'success' : 'danger') + ' pill-dot'}>{overall === 'pass' ? 'Pass' : 'Fail'}</span>}
                  </div>
                  {defs.map(t => {
                    const v = r.checks?.[t.id];
                    return (
                      <div key={t.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 92px', gap: 12, minHeight: 38, alignItems: 'center', padding: '5px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
                        <span style={{ fontSize: 14, color: 'var(--text-primary)' }}>{t.label}</span>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          {t.type === 'passfail'
                            ? (v ? <span className={'pill pill-' + (v === 'pass' ? 'success' : v === 'fail' ? 'danger' : 'neutral')}>{RC?.[v]?.l || v}</span>
                              : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Not run</span>)
                            : <span className="tnum" style={{ fontSize: 14, fontWeight: 600, color: v != null ? 'var(--warning-text)' : 'var(--text-muted)' }}>{v != null ? v : '—'}</span>}
                        </div>
                      </div>
                    );
                  })}
                  {r.notes && (
                    <div style={{ padding: '10px 16px', fontSize: 12, color: 'var(--text-primary)', background: 'var(--surface-inset)' }}>
                      <strong style={{ fontWeight: 600 }}>Notes:</strong> {r.notes}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}

      {/* ── QUARANTINE AND RMA ────────────────────────────────────────────── */}
      {tab === 'rma' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {dev.status === 'Quarantine' && (
            <div style={card}>
              <div style={cardHead}><span style={cardTitle}>In quarantine</span></div>
              {kv([
                ['Since', dev.quarantineDate ? fmt(dev.quarantineDate) : '—'],
                ['Failed checks', dev.quarantineFailedChecks?.length ? dev.quarantineFailedChecks.join(', ') : '—'],
                ['Note', dev.quarantineNote || '—'],
              ], 132)}
            </div>
          )}

          {dev.qc && (
            <div style={card}>
              <div style={cardHead}>
                <span style={cardTitle}>Goods-in QC</span>
                <span className={'pill pill-' + (dev.qc.result === 'pass' ? 'success' : 'danger') + ' pill-dot'}>
                  {dev.qc.result === 'pass' ? 'Passed' : 'Failed'}
                </span>
                <div style={{ flex: 1 }} />
                {dev.importBatch && onOpenQcBatch && (
                  <button className="btn btn-ghost btn-sm" onClick={() => onOpenQcBatch(dev.importBatch)}>Open batch {dev.importBatch}</button>
                )}
              </div>
              {kv([
                ['Checked', dev.qc.checkedAt ? fmt(dev.qc.checkedAt) : '—'],
                ['Checked by', dev.qc.by || '—'],
                ['Failed checks', dev.qc.failedChecks?.length ? dev.qc.failedChecks.join(', ') : 'None'],
                ['Note', dev.qc.note || '—'],
              ], 132)}
            </div>
          )}

          <div style={card}>
            <div style={cardHead}>
              <span style={cardTitle}>RMA history</span>
              <span className="tnum" style={{ fontSize: 12, color: 'var(--text-muted)' }}>{rmas.length}</span>
            </div>
            {rmas.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', fontSize: 14, color: 'var(--text-muted)' }}>
                {dev.status === 'Pending RMA'
                  ? 'Staged for RMA, but no shipment has been raised yet.'
                  : 'This unit has never been on an RMA.'}
              </div>
            ) : rmas.map(sh => (
              <div key={sh.id} style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 46, padding: '8px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
                <span className="tnum" style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', minWidth: 120 }}>{sh.ref || sh.id}</span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', flex: 1, minWidth: 0 }}>{sh.faultDescription || sh.faultCategory || '—'}</span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{fmt(sh.createdAt)}</span>
                <span className="pill pill-neutral">{sh.stage || sh.status || '—'}</span>
              </div>
            ))}
          </div>

          {dev.status === 'Retired' && (
            <div style={card}>
              <div style={cardHead}><span style={cardTitle}>Retired</span></div>
              {kv([
                ['Retired', dev.retiredAt ? fmt(dev.retiredAt) : '—'],
                ['By', dev.retiredBy || '—'],
                ['Reason', dev.retireReason || '—'],
              ], 132)}
            </div>
          )}
        </div>
      )}

      {/* ── MAINTENANCE ───────────────────────────────────────────────────── */}
      {tab === 'maintenance' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {maintLogs.length === 0 ? (
            <div style={{ ...card, padding: 28, textAlign: 'center', fontSize: 14, color: 'var(--text-muted)' }}>
              No maintenance has been logged against this unit.
            </div>
          ) : maintLogs.map((m, i) => (
            <div key={m.id || i} style={{ ...card, padding: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{fmtDT(m.date)}</span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>by {m.by || 'Unknown'}</span>
              </div>
              {m.actions.length > 0 && (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
                  {m.actions.map(a => <span key={a} className="pill pill-info" style={{ fontSize: 11 }}>{a}</span>)}
                </div>
              )}
              {m.description && <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>{m.description}</div>}
            </div>
          ))}
        </div>
      )}

      {/* ── ACTIVITY ──────────────────────────────────────────────────────── */}
      {tab === 'activity' && (
        <div style={card}>
          <div style={cardHead}>
            <span style={cardTitle}>Activity</span>
            <span className="tnum" style={{ fontSize: 12, color: 'var(--text-muted)' }}>{timeline.length}</span>
          </div>
          {timeline.length === 0 ? (
            <div style={{ padding: 28, textAlign: 'center', fontSize: 14, color: 'var(--text-muted)' }}>Nothing recorded yet.</div>
          ) : (
            <div style={{ padding: 16 }}>
              {timeline.map((e, i) => (
                <div key={i} style={{ display: 'flex', gap: 12 }}>
                  <div style={{ width: 16, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{ width: 9, height: 9, borderRadius: 9999, background: e.dot, marginTop: 5, flexShrink: 0 }} />
                    {i < timeline.length - 1 && <div style={{ flex: 1, width: 1, background: 'var(--border-default)', minHeight: 16 }} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0, paddingBottom: i < timeline.length - 1 ? 14 : 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>{e.title}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{fmtDT(e.date)} · {e.meta}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
